import React, { useState, useEffect, useCallback } from "react";

import { useWsNavigate, WorkspaceLink } from "../../hooks/useWsNavigate";
import { useAuth } from "../../contexts/AuthContext";
import {
  alertRulesAPI,
  alertEventsAPI,
  notificationChannelsAPI,
  metricsAPI,
} from "../../services/api";
import {
  Plus,
  Trash2,
  Bell,
  BellOff,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  AlertTriangle,
  Pencil,
  Slack,
  Mail,
  Webhook,
} from "lucide-react";

import ConditionRow from "../conditions/ConditionRow";
import CallPropertyBuilder from "../filters/CallPropertyBuilder";
import PageHeader from "../layout/PageHeader";

// Icon for a notification channel by type (Slack/email/webhook).
const ChannelIcon = ({ type, className = "h-4 w-4" }) => {
  if (type === "slack") return <Slack className={className} />;
  if (type === "email") return <Mail className={className} />;
  if (type === "webhook") return <Webhook className={className} />;
  return <Bell className={className} />;
};

const NOTIFICATIONS_BASE = "/observability/notifications";

const AlertsSettings = ({ selectedAgent, agentPicker, mode = "list", editId = null }) => {
  const { currentWorkspace } = useAuth();
  const navigate = useWsNavigate();
  const clientId = currentWorkspace?.id;

  // The route drives whether the create/edit form is shown (like /ai-testing-agent/create).
  const showCreateForm = mode !== "list";
  const editingRuleId = mode === "edit" ? editId : null;

  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRule, setExpandedRule] = useState(null);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewResults, setPreviewResults] = useState(null);
  const [showCallPropBuilder, setShowCallPropBuilder] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    name: "",
    description: "",
    agent_id: null,
    schedule_frequency: "every_30_minutes",
    time_window_minutes: 30,
    conditions: [{ metric_id: null, metric_key: null, field: null, operator: "equals", value: "" }],
    notification_channel_ids: [],
  });

  const fetchAll = useCallback(async () => {
    if (!clientId) return;
    try {
      const eventParams = { per_page: 20 };
      if (selectedAgent?.id) eventParams.agent_id = selectedAgent.id;
      const [rulesData, eventsData, channelsData, metricsData] = await Promise.all([
        alertRulesAPI.list(clientId),
        alertEventsAPI.list(clientId, eventParams),
        notificationChannelsAPI.list(clientId),
        metricsAPI.getAllMetrics(clientId),
      ]);
      setRules(rulesData);
      setEvents(eventsData.alert_events || []);
      setChannels(channelsData);
      // Combine all metric categories
      const allMetrics = [
        ...(metricsData.standard_metrics || []),
        ...(metricsData.global_metrics || []),
        ...(metricsData.data || []),
      ];
      setMetrics(allMetrics);
    } catch (e) {
      console.error("Failed to load alerts data", e);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedAgent?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleConditionChange = (index, updates) => {
    setForm((f) => {
      const conditions = [...f.conditions];
      conditions[index] = { ...conditions[index], ...updates };
      return { ...f, conditions };
    });
  };

  const handleAddCondition = () => {
    setForm((f) => ({
      ...f,
      conditions: [...f.conditions, { metric_id: null, metric_key: null, field: null, operator: "equals", value: "" }],
    }));
  };

  const handleRemoveCondition = (index) => {
    setForm((f) => ({
      ...f,
      conditions: f.conditions.filter((_, i) => i !== index),
    }));
  };

  const handleToggleChannel = (channelId) => {
    setForm((f) => {
      const ids = f.notification_channel_ids.includes(channelId)
        ? f.notification_channel_ids.filter((id) => id !== channelId)
        : [...f.notification_channel_ids, channelId];
      return { ...f, notification_channel_ids: ids };
    });
  };

  const resetForm = useCallback(() => {
    setForm({ name: "", description: "", agent_id: null, schedule_frequency: "every_30_minutes", time_window_minutes: 30, conditions: [{ metric_id: null, operator: "equals", value: "" }], notification_channel_ids: [] });
    setShowCallPropBuilder(false);
  }, []);

  // Populate the form from an existing rule (edit mode). Resolves metric_key → metric_id,
  // and migrates legacy single-sub conditions into the sub_criteria[] array the editor uses.
  const populateForm = useCallback((rule) => {
    const resolvedConditions = (rule.conditions || []).map((raw) => {
      let c = raw;
      if (!c.metric_id && c.metric_key) {
        const m = metrics.find((x) => x.standard_metric_key === c.metric_key);
        if (m) c = { ...c, metric_id: m.id };
      }
      if (!Array.isArray(c.sub_criteria) && c.sub_metric) {
        c = {
          ...c,
          sub_criteria: [{ sub_metric: c.sub_metric, sub_property: c.sub_property || "metric_value", value: c.value }],
        };
      }
      return c;
    });

    setForm({
      name: rule.name || "",
      description: rule.description || "",
      agent_id: rule.agent_id,
      schedule_frequency: rule.schedule_frequency || "every_30_minutes",
      time_window_minutes: rule.time_window_minutes || 30,
      conditions: resolvedConditions.length > 0 ? resolvedConditions : [{ metric_id: null, operator: "equals", value: "" }],
      notification_channel_ids: rule.notification_channel_ids || [],
    });
    setShowCallPropBuilder(false);
  }, [metrics]);

  // Sync the form to the current route mode: a fresh form for /new, and the selected
  // rule's values for /:id/edit (once rules have loaded).
  useEffect(() => {
    if (mode === "new") {
      resetForm();
      setPreviewResults(null);
    }
  }, [mode, resetForm]);

  useEffect(() => {
    if (mode === "edit" && editingRuleId && rules.length) {
      const rule = rules.find((r) => r.id === editingRuleId);
      if (rule) populateForm(rule);
    }
  }, [mode, editingRuleId, rules, populateForm]);

  const handleCreate = async () => {
    if (!form.name.trim()) return flash("Name is required", "error");
    const hasCondition = form.conditions.some((c) => c.metric_id || c.property);
    if (!hasCondition) return flash("Add at least one condition", "error");
    if (form.notification_channel_ids.length === 0) return flash("Select at least one notification channel", "error");

    setSaving(true);
    try {
      const payload = {
        alert_rule: {
          name: form.name,
          description: form.description,
          scope: "observability",
          agent_id: form.agent_id || selectedAgent?.id || null,
          schedule_frequency: form.schedule_frequency,
          time_window_minutes: form.time_window_minutes,
          conditions: form.conditions,
        },
        notification_channel_ids: form.notification_channel_ids,
      };

      if (editingRuleId) {
        await alertRulesAPI.update(clientId, editingRuleId, payload);
        flash("Alert rule updated");
      } else {
        await alertRulesAPI.create(clientId, payload);
        flash("Alert rule created");
      }

      resetForm();
      fetchAll();
      navigate(NOTIFICATIONS_BASE);
    } catch (e) {
      flash(e.response?.data?.errors?.join(", ") || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      await alertRulesAPI.toggle(clientId, ruleId);
      fetchAll();
    } catch (e) {
      flash(e.message, "error");
    }
  };

  const handleDeleteRule = async (ruleId, name) => {
    if (!window.confirm(`Delete alert rule "${name}"?`)) return;
    try {
      await alertRulesAPI.destroy(clientId, ruleId);
      flash(`Rule "${name}" deleted`);
      fetchAll();
    } catch (e) {
      flash(e.message, "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      {message && (
        <div className={`rounded-lg px-4 py-3 mb-4 text-sm flex items-center gap-2 ${message.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {message.type === "error" ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      {/* No channels warning */}
      {channels.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center gap-3 text-sm text-amber-800">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
          <span>No notification channels configured. <WorkspaceLink to="/settings/integrations" className="font-medium underline">Set up Slack, email, or webhook channels first</WorkspaceLink> in the Integrations tab.</span>
        </div>
      )}

      {/* Page header — description + (on the list) the agent picker and New Rule button.
          Matches the standard page layout (PageHeader handles the mb-6 spacing). */}
      <PageHeader
        description={showCreateForm
          ? "Set up conditions and a schedule to get notified when calls match."
          : "Trigger notifications when metric conditions are met."}
      >
        {!showCreateForm && (
          <>
            {agentPicker}
            <button onClick={() => navigate(`${NOTIFICATIONS_BASE}/new`)} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black items-center gap-2">
              <Plus className="h-4 w-4" /> New Rule
            </button>
          </>
        )}
      </PageHeader>

      {/* ── Alert Rules ──────────────────────────────────────────────── */}
      <section>
        {/* Create form — the page title lives in the layout breadcrumb (← Notifications › …) */}
        {showCreateForm && (
          <div className="border border-border rounded-lg bg-card mb-6">
            {/* Subtitle (title is shown in the breadcrumb) */}
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm text-muted-foreground">Set up conditions and schedule to get notified when calls match.</p>
            </div>

            <div className="p-4 space-y-6">
              {/* Name + Description */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Rule Name</label>
                  <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g., High Latency Alert"
                    className="w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description <span className="text-muted-foreground">(optional)</span></label>
                  <input type="text" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Alert when response time is too high"
                    className="w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" />
                </div>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Schedule</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-muted-foreground">Run</span>
                  <select value={form.schedule_frequency} onChange={(e) => setForm((f) => ({ ...f, schedule_frequency: e.target.value }))}
                    className="rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm">
                    <option value="every_15_minutes">every 15 minutes</option>
                    <option value="every_30_minutes">every 30 minutes</option>
                    <option value="every_hour">every hour</option>
                    <option value="every_4_hours">every 4 hours</option>
                    <option value="every_12_hours">every 12 hours</option>
                    <option value="daily">daily</option>
                  </select>
                  <span className="text-sm text-muted-foreground">and check calls from the last</span>
                  <div className="flex items-center gap-1.5">
                    <input type="number" value={form.time_window_minutes}
                      onChange={(e) => setForm((f) => ({ ...f, time_window_minutes: parseInt(e.target.value) || 30 }))}
                      min={5} className="w-20 rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" />
                    <span className="text-sm text-muted-foreground">minutes</span>
                  </div>
                </div>
              </div>

              {/* Conditions */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Conditions <span className="text-muted-foreground font-normal">(all must match)</span></label>

                <div className="space-y-2">
                  {form.conditions.map((cond, i) => {
                    if (cond.type === "call_property") {
                      return (
                        <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                          <span className="text-sm text-amber-800 flex-1">
                            <span className="font-medium">{cond.property?.replace("metadata.", "metadata: ")}</span>
                            {" "}<span className="text-amber-600">{cond.operator?.replace(/_/g, " ")}</span>{cond.value ? ` ${cond.value}` : ""}
                          </span>
                          <button onClick={() => handleRemoveCondition(i)} className="text-muted-foreground hover:text-red-500 text-lg leading-none">&times;</button>
                        </div>
                      );
                    }
                    return (
                      <ConditionRow key={i} condition={cond} index={i} metrics={metrics} onChange={handleConditionChange} onRemove={form.conditions.length > 1 ? handleRemoveCondition : undefined} />
                    );
                  })}
                </div>

                {/* Add condition buttons */}
                <div className="flex gap-3 mt-3">
                  <button onClick={handleAddCondition} className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> Metric condition
                  </button>
                  <button onClick={() => setShowCallPropBuilder((v) => !v)}
                    className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> Call property condition
                  </button>
                </div>

                {/* Inline call-property builder — produces a complete condition on Apply */}
                {showCallPropBuilder && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Call property condition</p>
                      <button onClick={() => setShowCallPropBuilder(false)} className="text-xs text-muted-foreground hover:text-muted-foreground">Cancel</button>
                    </div>
                    <CallPropertyBuilder
                      inline
                      onApply={(cond) => {
                        setForm((f) => ({ ...f, conditions: [...f.conditions, cond] }));
                        setShowCallPropBuilder(false);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Notification channels */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notify via</label>
                {channels.length === 0 ? (
                  <div className="bg-muted/50 border border-border rounded-md px-4 py-3 text-sm text-muted-foreground flex items-center justify-between">
                    <span>No notification channels configured yet.</span>
                    <WorkspaceLink to="/settings/integrations" className="text-black font-medium hover:underline">Set up channels →</WorkspaceLink>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {channels.map((ch) => (
                      <button key={ch.id} onClick={() => handleToggleChannel(ch.id)}
                        className={`px-4 py-2 text-sm rounded-md border flex items-center gap-2 transition-colors ${
                          form.notification_channel_ids.includes(ch.id)
                            ? "bg-black border-black text-white"
                            : "bg-card border-input text-foreground hover:border-gray-400"
                        }`}>
                        <ChannelIcon type={ch.channel_type} className="h-4 w-4" />
                        <span>{ch.name}</span>
                        {form.notification_channel_ids.includes(ch.id) && (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ))}
                    <WorkspaceLink to="/settings/integrations" className="px-4 py-2 text-sm rounded-md border border-dashed border-input text-muted-foreground hover:text-muted-foreground hover:border-gray-400 flex items-center gap-1">
                      <Plus className="h-3.5 w-3.5" /> Add channel
                    </WorkspaceLink>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="px-4 py-3 border-t border-border flex items-center gap-3">
              <button onClick={handleCreate} disabled={saving}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingRuleId ? "Save Changes" : "Create Rule"}
              </button>
              <button
                onClick={async () => {
                  const hasConditions = form.conditions.some((c) => c.metric_id || c.property);
                  if (!hasConditions) return;
                  setPreviewing(true);
                  setPreviewResults(null);
                  try {
                    const res = await alertRulesAPI.preview(clientId, form.conditions, form.agent_id);
                    setPreviewResults(res);
                  } catch (e) {
                    setPreviewResults({ matches: [], total_checked: 0, error: e.response?.data?.error || "Preview failed" });
                  } finally {
                    setPreviewing(false);
                  }
                }}
                disabled={previewing || !form.conditions.some((c) => c.metric_id || c.property)}
                className="inline-flex justify-center py-2 px-4 border border-input shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
              >
                {previewing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Preview Matching Calls
              </button>
              <button onClick={() => { setPreviewResults(null); navigate(NOTIFICATIONS_BASE); }}
                className="py-2 px-4 text-sm text-muted-foreground hover:text-foreground">
                Cancel
              </button>
            </div>

            {/* Preview results */}
            {previewResults && (
              <div className="mt-3 border border-indigo-100 rounded-lg bg-indigo-50/50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">
                    {previewResults.matches?.length || 0} of {previewResults.total_checked} recent calls would trigger this rule
                  </span>
                  <button onClick={() => setPreviewResults(null)} className="text-xs text-muted-foreground hover:text-muted-foreground">dismiss</button>
                </div>
                {previewResults.error && (
                  <p className="text-xs text-red-500">{previewResults.error}</p>
                )}
                {previewResults.matches?.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {previewResults.matches.map((m) => (
                      <a
                        key={m.id}
                        href={`/observability/conversations/${m.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between text-xs bg-card rounded px-3 py-1.5 hover:bg-muted/50 border border-border"
                      >
                        <span className="text-foreground">
                          Call #{m.id} {m.agent_name && `· ${m.agent_name}`}
                        </span>
                        <span className="text-muted-foreground">
                          {m.call_timestamp ? new Date(m.call_timestamp).toLocaleDateString() : ""}
                        </span>
                      </a>
                    ))}
                  </div>
                )}
                {previewResults.matches?.length === 0 && !previewResults.error && (
                  <p className="text-xs text-muted-foreground">No recent calls match these conditions.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Existing rules — filtered by selected agent. Hidden while creating/editing
            so the create view shows only create-related content. */}
        {!showCreateForm && (() => {
          // Filtered by the page's agent picker. Rules with no agent (account-wide) always show.
          const filteredRules = selectedAgent
            ? rules.filter((r) => !r.agent_id || r.agent_id === selectedAgent.id)
            : rules;
          // Most recent time each rule actually FIRED (created an event), from the
          // events list — distinct from last_run_at (last time it was *checked*).
          const lastFiredByRule = {};
          events.forEach((e) => {
            const prev = lastFiredByRule[e.alert_rule_id];
            if (!prev || new Date(e.created_at) > new Date(prev)) lastFiredByRule[e.alert_rule_id] = e.created_at;
          });
          return filteredRules.length === 0 && !showCreateForm ? (
          <div className="text-center py-16 border border-border rounded-lg">
            <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No alert rules yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create one to get notified when calls match your conditions.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRules.map((rule) => (
              <div key={rule.id} className="border border-border rounded-lg hover:border-input transition-colors">
                <div className="px-5 py-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedRule(expandedRule === rule.id ? null : rule.id)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${rule.enabled ? "bg-green-500" : "bg-gray-300"}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-foreground">{rule.name}</span>
                        {rule.agent_name && <span className="text-sm text-muted-foreground">· {rule.agent_name}</span>}
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5">
                        Runs {rule.schedule_frequency?.replace(/_/g, " ")} · checks last {rule.time_window_minutes} min
                        {rule.last_run_at && ` · last checked ${new Date(rule.last_run_at).toLocaleString()}`}
                        {lastFiredByRule[rule.id] && ` · last fired ${new Date(lastFiredByRule[rule.id]).toLocaleString()}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex gap-1.5">
                      {rule.notification_channels?.map((ch) => (
                        <span key={ch.id} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground inline-flex items-center gap-1">
                          <ChannelIcon type={ch.channel_type} className="h-3 w-3" />{ch.name}
                        </span>
                      ))}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); navigate(`${NOTIFICATIONS_BASE}/${rule.id}/edit`); }}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-indigo-600 hover:bg-indigo-50"
                      title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleToggleRule(rule.id); }}
                      className={`p-1.5 rounded-md ${rule.enabled ? "text-amber-500 hover:bg-amber-50" : "text-green-500 hover:bg-green-50"}`}
                      title={rule.enabled ? "Disable" : "Enable"}>
                      {rule.enabled ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteRule(rule.id, rule.name); }}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {expandedRule === rule.id ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </div>
                {expandedRule === rule.id && (
                  <div className="px-5 pb-4 border-t border-border pt-3">
                    {rule.description && <p className="text-sm text-muted-foreground mb-3">{rule.description}</p>}
                    <p className="text-sm font-medium text-foreground mb-2">Conditions:</p>
                    <div className="flex flex-wrap gap-2">
                      {rule.conditions.map((c, i) => {
                        const isCallProp = c.type === "call_property";
                        const metric = !isCallProp ? metrics.find((m) => m.id === c.metric_id) : null;
                        const subs = Array.isArray(c.sub_criteria) ? c.sub_criteria : [];
                        return (
                          <span key={i} className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border ${
                            isCallProp ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-indigo-50 border-indigo-200 text-indigo-800"
                          }`}>
                            <span className="font-medium">{isCallProp ? c.property?.replace("metadata.", "metadata: ") : (metric?.name || c.metric_key || `#${c.metric_id}`)}</span>
                            {c.field && <span className="opacity-60">({c.field})</span>}
                            {subs.length > 0 ? (
                              <span className="opacity-80">
                                {subs.map((sc) => `${(sc.sub_metric || "").replace(/_/g, " ")} ${sc.sub_property === "outcome_classification" ? "class" : "="} ${sc.value}`).join(" · ")}
                              </span>
                            ) : (
                              <>
                                {c.operator && <span className="opacity-60">{c.operator.replace(/_/g, " ")}</span>}
                                {c.value != null && c.value !== "" && <span className="font-semibold">{c.value}</span>}
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
        })()}
      </section>

      {/* ── Recent Alert Events (collapsible) ─────────────────────────── */}
      {!showCreateForm && (
      <section className="mt-8">
        <button
          onClick={() => setEventsExpanded((v) => !v)}
          className="group flex items-center gap-2 mb-3"
        >
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${eventsExpanded ? "rotate-90" : ""}`}
          />
          <h3 className="text-sm font-semibold text-foreground">Recent Alert Events</h3>
          {events.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
              {events.length}
            </span>
          )}
        </button>

        {eventsExpanded && (
          events.length === 0 ? (
            <p className="text-sm text-muted-foreground ml-6">No alerts have fired yet.</p>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-gray-100">
              {events.map((ev) => {
                const mc = ev.matched_conditions || {};
                const matchCount = mc.match_count || 1;
                return (
                  <div key={ev.id} className="px-5 py-3 flex items-center justify-between hover:bg-muted/50">
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-foreground">{ev.alert_rule_name}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        — {matchCount} call{matchCount !== 1 ? "s" : ""} matched
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <a href={`/observability/conversations?alert_event_id=${ev.id}`}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                        View calls
                      </a>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${ev.notification_status === "sent" ? "bg-green-50 text-green-700 border-green-200" : ev.notification_status === "failed" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {ev.notification_status}
                      </span>
                      <span className="text-xs text-muted-foreground">{new Date(ev.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </section>
      )}
    </div>
  );
};

export default AlertsSettings;
